/**
 * スネークケースをキャメルケースに変更する
 * 参考：https://stackoverflow.com/questions/2970525/converting-any-string-into-camel-case
 *
 * @param str スネークケースをキャメルケースに変更する
 * @returns スネークケースをキャメルケースに変換したもの
 */
export const convertSnakeCaseToCamelCase = (
  str: string | undefined,
): string => {
  if (str === undefined) {
    return '';
  }
  const camelCaseStr = str.replace(/_./g, (word) => word[1].toUpperCase());

  return camelCaseStr;
};

/**
 * スネークケースのキー文字列をキャメルケースに変換する（内部用）
 *
 * @param key スネークケースの文字列
 * @returns キャメルケースに変換した文字列
 */
const snakeToCamelKey = (key: string): string =>
  key.replace(/_+([a-zA-Z0-9])/g, (_, c: string) => c.toUpperCase());

/**
 * オブジェクトのキーを再帰的にスネークケースからキャメルケースに変換する。
 * 値（文字列・数値・真偽値など）はそのまま保持される。
 * 配列・ネストしたオブジェクト・null・プリミティブすべてに対応する。
 *
 * @param input 変換対象（オブジェクト・配列・プリミティブ）
 * @returns キーをキャメルケースに変換した値
 */
export const convertObjectKeysToCamelCase = <T = unknown>(
  input: unknown,
): T => {
  if (Array.isArray(input)) {
    return input.map((item) => convertObjectKeysToCamelCase(item)) as T;
  }
  if (input !== null && typeof input === 'object') {
    return Object.fromEntries(
      Object.entries(input as Record<string, unknown>).map(([key, value]) => [
        snakeToCamelKey(key),
        convertObjectKeysToCamelCase(value),
      ]),
    ) as T;
  }

  return input as T;
};

/**
 * キャメルケースをスネークケースに変更する
 *
 * @param str キャメルケースをスネークケースに変更する
 * @returns キャメルケースをスネークケースに変換したもの
 */
export const convertCamelCaseToSnakeCase = (
  str: string | undefined,
): string => {
  if (str === undefined) {
    return '';
  }

  const snakeCaseStr = str.replaceAll(
    /.[A-Z]/g,
    (word) => `${word[0]}_${word[1].toLowerCase()}`,
  );

  return snakeCaseStr;
};
